import { ArrowLeft } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import type { GetStaticPaths, GetStaticProps } from "next"
import Image from "next/image"
import Container from "@/components/common/Container"
import Layout from "@/components/common/Layout"
import MarketingHeading from "@/components/common/MarketingHeading"
import { PageMeta } from "@/components/common/Seo/PageMeta"
import MDXRenderer from "@/components/utils/mdx/MDXRenderer"
import {
  type CaseStudy,
  getAllCaseStudiesMeta,
  getCaseStudyBySlug,
} from "@/utils/mdx-remote"

interface CustomerCaseStudyPageProps {
  caseStudy: CaseStudy | null
}

export const getStaticPaths: GetStaticPaths = async () => {
  const caseStudies = getAllCaseStudiesMeta()

  return {
    paths: caseStudies.map((caseStudy) => ({
      params: { id: caseStudy.slug },
    })),
    fallback: false, // Return 404 for paths not returned by getStaticPaths
  }
}

export const getStaticProps: GetStaticProps<
  CustomerCaseStudyPageProps
> = async ({ params }) => {
  const slug = params?.id as string
  const caseStudy = await getCaseStudyBySlug(slug)

  // If we couldn't find the case study, return a 404
  if (!caseStudy) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      caseStudy,
    },
  }
}

const CustomerCaseStudyPage = ({ caseStudy }: CustomerCaseStudyPageProps) => {
  if (!caseStudy) {
    return <div>Case study not found</div>
  }

  return (
    <Layout>
      <PageMeta
        meta={{
          title: caseStudy.title,
          description: caseStudy.description,
          image: caseStudy.thumbnail,
          type: "article",
        }}
      />
      <div className="py-12 max-w-[860px] mx-auto">
        <Container>
          <div>
            <LinkButton
              href="/customers"
              variant="ghost"
              className="inline-flex no-underline mb-4"
            >
              <Icon as={ArrowLeft} />
              Back to Case Studies
            </LinkButton>
          </div>

          <div className="relative w-full h-[200px] md:h-[300px] lg:h-[400px] mb-16 rounded-xl overflow-hidden">
            <Image
              src={caseStudy.thumbnail}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
              alt={caseStudy.title}
              style={{
                objectFit: "cover",
                objectPosition: "center",
              }}
              priority
              className="transition-transform duration-700 hover:scale-105"
            />
          </div>
          <div className="flex flex-col items-start">
            <Badge
              variant="outline"
              className="inline-block mb-4 font-normal text-fg-secondary!"
            >
              {caseStudy.tag}
            </Badge>
            <div className="flex items-center gap-2 mb-4">
              <MarketingHeading level={1} className="text-3xl md:text-5xl">
                {caseStudy.title}
              </MarketingHeading>
            </div>
          </div>
          <MDXRenderer mdxSource={caseStudy.mdxSource} />
        </Container>
      </div>
    </Layout>
  )
}

export default CustomerCaseStudyPage
